import React, { useEffect, useMemo, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import { feature } from 'topojson-client';
import countries110m from 'world-atlas/countries-110m.json';
import {
  addDistortionToCountries,
  buildDistortionHeatPoints,
  buildGraticulePaths,
  buildIndicatrixPaths,
  computeProjectionBounds,
  heatColor,
  warpFeatureGeometry,
} from '../../utils/geoWarp';
import { getProjectionById, projectionForDistortion, transformCoordinateSet } from '../../utils/projectionCatalog';

const fitView = (globeRef, projectionId) => {
  if (!globeRef.current) {
    return;
  }

  if (projectionId === 'epsg3006') {
    globeRef.current.pointOfView({ lat: 62.2, lng: 15.5, altitude: 1.2 }, 1400);
    return;
  }

  if (projectionId === 'polarStereo') {
    globeRef.current.pointOfView({ lat: 88, lng: 0, altitude: 1.4 }, 1400);
    return;
  }

  globeRef.current.pointOfView({ lat: 20, lng: 10, altitude: 1.8 }, 1200);
};

export default function GlobeScene({ projectionId, showIndicatrix, showHeatmap, onInspectCoordinate }) {
  const globeRef = useRef(null);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  const countries = useMemo(() => {
    const world = feature(countries110m, countries110m.objects.countries);
    return world.features || [];
  }, []);

  useEffect(() => {
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fitView(globeRef, projectionId);
  }, [projectionId]);

  useEffect(() => {
    if (!globeRef.current) {
      return;
    }

    const controls = globeRef.current.controls();
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 140;
    controls.maxDistance = 420;
  }, []);

  const projection = getProjectionById(projectionId);
  const distortionProjector = projectionForDistortion(projectionId);

  const bounds = useMemo(() => {
    if (!countries.length) {
      return null;
    }
    return computeProjectionBounds(countries, projection.projector);
  }, [countries, projection]);

  const warpedCountries = useMemo(() => {
    if (!countries.length || !bounds) {
      return [];
    }

    const remapped = countries
      .map((country) => warpFeatureGeometry(country, projection.projector, bounds))
      .filter(Boolean);

    return addDistortionToCountries(remapped, distortionProjector);
  }, [countries, bounds, projection, distortionProjector]);

  const pathData = useMemo(() => {
    if (!bounds) {
      return [];
    }

    const graticules = buildGraticulePaths(projection.projector, bounds);
    if (!showIndicatrix) {
      return graticules;
    }

    return [...graticules, ...buildIndicatrixPaths(projection.projector, bounds)];
  }, [bounds, projection, showIndicatrix]);

  const heatPoints = useMemo(() => {
    if (!showHeatmap || !bounds) {
      return [];
    }
    return buildDistortionHeatPoints(distortionProjector, bounds);
  }, [showHeatmap, bounds, distortionProjector]);

  const onGlobeClick = (coords) => {
    if (!coords) {
      return;
    }

    onInspectCoordinate(transformCoordinateSet(coords.lat, coords.lng));
  };

  return (
    <div className="globe-scene-wrap">
      <Globe
        ref={globeRef}
        width={viewport.width}
        height={viewport.height}
        globeImageUrl={`${process.env.PUBLIC_URL}/earth-blue-marble.jpg`}
        bumpImageUrl={`${process.env.PUBLIC_URL}/earth-topology.png`}        showAtmosphere
        atmosphereColor="#5dc3ff"
        atmosphereAltitude={0.22}
        polygonsData={warpedCountries}
        polygonCapColor={(feature) => heatColor(feature.properties.distortionScore)}
        polygonSideColor={() => 'rgba(8, 16, 32, 0.85)'}
        polygonStrokeColor={() => 'rgba(26, 59, 92, 0.44)'}
        polygonAltitude={(feature) => 0.004 + feature.properties.distortionScore * 0.07}
        polygonsTransitionDuration={1400}
        pathsData={pathData}
        pathPoints="coords"
        pathPointLat="lat"
        pathPointLng="lng"
        pathColor={(path) =>
          path.kind === 'indicatrix'
            ? `rgba(${Math.round(240 * path.score + 20)}, ${Math.round(210 - 110 * path.score)}, ${Math.round(220 - 130 * path.score)}, 0.95)`
            : 'rgba(145, 200, 255, 0.72)'
        }
        pathStroke={(path) => (path.kind === 'indicatrix' ? 1.05 : 0.65)}
        pathDashLength={(path) => (path.kind === 'indicatrix' ? 0.18 : 0.08)}
        pathDashGap={(path) => (path.kind === 'indicatrix' ? 0.09 : 0.04)}
        pathDashAnimateTime={showIndicatrix ? 1800 : 0}
        pointsData={heatPoints}
        pointColor={(point) => heatColor(point.score)}
        pointAltitude={(point) => 0.01 + point.score * 0.03}
        pointRadius={0.55}
        pointsMerge
        onGlobeClick={onGlobeClick}
      />
    </div>
  );
}
